import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router';

import { AISuggestion } from '@ds/ai';
import { Button, Card, Divider, Icon, KpiCard, Progress } from '@ds/core';
import { AppointmentCard, TaskCard } from '@ds/data';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { dataPorExtenso, diaIso, horaDe, saudacao } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { Agendamento, ResumoDoDia, RetornoEmAberto } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { atual } from '@/session/sessao';
import { NovoAgendamentoModal } from './NovoAgendamentoModal';

export interface CargaDoInicio {
    resumo: ResumoDoDia;
    /* A lista de quem não voltou vem junto: é a primeira ação do dia de quem
       abre esta tela, e não vale mandar procurar em outro módulo. */
    retornos: RetornoEmAberto[];
}

export async function carregarInicio(): Promise<CargaDoInicio> {
    const [resumo, retornos] = await Promise.all([
        dados().resumoDoDia(),
        dados().retornosEmAberto(90),
    ]);
    return { resumo, retornos };
}

/** Primeiro nome, para a saudação. "Ana Prado" vira "Ana". */
function primeiroNome(nome: string): string {
    return nome.trim().split(/\s+/)[0] ?? nome;
}

export function Inicio() {
    const { resumo, retornos } = useLoaderData() as CargaDoInicio;
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const sessao = atual();
    const [novoAberto, setNovoAberto] = useState(false);
    /* As tarefas ficam em estado local para o clique responder na hora; a porta
       é chamada em seguida e, se recusar, o estado volta. */
    const [tarefas, setTarefas] = useState(resumo.tarefas);

    const irPara = (moduloId: string) => {
        const rota = rotaPorId(moduloId);
        if (rota) navigate(rota.path);
    };

    const alternar = async (id: string, concluida: boolean) => {
        setTarefas((atuais) => atuais.map((t) => (t.id === id ? { ...t, concluida } : t)));
        try {
            await dados().alternarTarefa(id, concluida);
        } catch {
            setTarefas((atuais) => atuais.map((t) => (t.id === id ? { ...t, concluida: !concluida } : t)));
            aviso({ title: 'Não foi possível atualizar a tarefa', tone: 'danger' });
        }
    };

    const agenda = resumo.agendaDeHoje;
    const hoje = new Date();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <PageHeader
                title={`${saudacao(hoje)}, ${sessao ? primeiroNome(sessao.nome) : 'bem-vinda'}`}
                description={`${dataPorExtenso(hoje)} · ${agenda.length} ${agenda.length === 1 ? 'atendimento previsto' : 'atendimentos previstos'}`}
                actions={
                    <>
                        <ExportMenu<Agendamento>
                            label="Exportar" size="md"
                            payload={{
                                title: 'Resumo do dia',
                                subtitle: dataPorExtenso(hoje),
                                columns: [
                                    { header: 'Horário', value: (a) => horaDe(a.inicioEm) },
                                    { header: 'Paciente', value: (a) => a.paciente },
                                    { header: 'Procedimento', value: (a) => a.procedimento },
                                    { header: 'Profissional', value: (a) => a.profissional },
                                    { header: 'Sala', value: (a) => a.sala ?? '' },
                                    { header: 'Status', value: (a) => a.status },
                                ],
                                rows: agenda,
                            }}
                        />
                        <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setNovoAberto(true)}>Novo agendamento</Button>
                    </>
                }
            />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 14 }}>
                {resumo.indicadores.map((i) => (
                    <KpiCard
                        key={i.id} label={i.rotulo} value={i.valor} delta={i.variacao}
                        deltaTone={i.tendencia} hint={i.apoio} icon={<Icon name={i.icone} size={16} />}
                    />
                ))}
            </div>

            {resumo.sugestao && (
                <AISuggestion
                    text={resumo.sugestao.texto}
                    onAccept={resumo.sugestao.moduloDestino ? () => irPara(resumo.sugestao?.moduloDestino ?? '') : undefined}
                />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'var(--layout-colunas, minmax(0,1.4fr) minmax(0,1fr))', gap: 16, alignItems: 'start' }}>
                <Card
                    title="Agenda de hoje"
                    subtitle={[...new Set(agenda.map((a) => a.sala).filter(Boolean))].join(' e ') || 'Sem salas ocupadas'}
                    actions={<Button variant="link" onClick={() => irPara('agenda')}>Ver agenda</Button>}
                    padding={18}
                >
                    <div className="vec-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {agenda.map((a) => (
                            <AppointmentCard
                                key={a.id} time={horaDe(a.inicioEm)} patient={a.paciente} procedure={a.procedimento}
                                professional={a.profissional} room={a.sala} status={a.status}
                                onClick={() => navigate(`/app/contatos/${a.pacienteId}`)}
                            />
                        ))}
                    </div>
                </Card>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Card title="Tarefas" subtitle="Follow-ups do dia" padding={18}>
                        <div className="vec-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {tarefas.map((t) => (
                                <TaskCard
                                    key={t.id} title={t.titulo} owner={t.responsavel} priority={t.prioridade} done={t.concluida}
                                    due={t.venceEm === diaIso(hoje) ? 'Hoje' : t.venceEm.split('-').reverse().slice(0, 2).join('/')}
                                    onToggle={(concluida) => void alternar(t.id, concluida)}
                                />
                            ))}
                        </div>
                    </Card>

                    {/* Sugestão de follow-up com nome e telefone: é o que
                        transforma "3 pacientes sem retorno" em três ligações. */}
                    {retornos.length > 0 && (
                        <Card
                            title="Sem retorno"
                            subtitle={`${retornos.length} ${retornos.length === 1 ? 'paciente atendido' : 'pacientes atendidos'} há mais de 90 dias e sem nada marcado`}
                            padding={18}
                        >
                            <div className="vec-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {retornos.slice(0, 5).map((r) => (
                                    <button
                                        key={r.contatoId} type="button"
                                        onClick={() => navigate(`/app/contatos/${r.contatoId}`)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                                            padding: '8px 10px', border: '1px solid var(--border)',
                                            borderRadius: 'var(--radius-sm)', background: 'var(--surface)',
                                            cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)',
                                        }}
                                    >
                                        <Icon name="user-round-x" size={15} />
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <strong style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{r.nome}</strong>
                                            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                                                {r.ultimoProcedimento} · {r.diasSemRetorno} dias
                                                {r.telefone ? ` · ${r.telefone}` : ''}
                                            </span>
                                        </span>
                                        <Icon name="chevron-right" size={15} />
                                    </button>
                                ))}
                            </div>
                            {retornos.length > 5 && (
                                <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                    e mais {retornos.length - 5} — a régua completa está em Marketing, no público
                                    “sem retorno”.
                                </p>
                            )}
                        </Card>
                    )}

                    {resumo.onboarding && (
                        <Card title="Onboarding da clínica" padding={18}>
                            <Progress
                                label={`${resumo.onboarding.etapasConcluidas} de ${resumo.onboarding.etapasTotais} etapas concluídas`}
                                value={Math.round((resumo.onboarding.etapasConcluidas / resumo.onboarding.etapasTotais) * 100)}
                                showValue
                            />
                            <Divider spacing={14} />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Próxima: {resumo.onboarding.proximaEtapa}</span>
                                <Button size="sm" variant="secondary" onClick={() => irPara(resumo.onboarding?.moduloDaProximaEtapa ?? '')}>Continuar</Button>
                            </div>
                        </Card>
                    )}
                </div>
            </div>

            <NovoAgendamentoModal
                open={novoAberto} onClose={() => setNovoAberto(false)} dataPadrao={diaIso(hoje)}
                /* Recarrega pelo loader da rota: a agenda de hoje é do servidor,
                   não do estado da tela — inventar o item na lista local faria a
                   tela e a fonte divergirem. */
                onCriado={() => revalidator.revalidate()}
            />
        </div>
    );
}
